import { ApiError, isAuthErrorBody } from '@monecolier/api-client';

export type AuthFormErrorKind = 'generic' | 'locked' | 'network';

export interface AuthFormError {
  kind: AuthFormErrorKind;
  message: string;
}

const NETWORK_ERROR_MESSAGE =
  "Impossible de contacter le serveur. Vérifiez votre connexion, ou réessayez dans " +
  'un instant. Si le problème persiste, signalez-le (indice technique dans la ' +
  'console du navigateur).';

const UNKNOWN_ERROR_MESSAGE = 'Une erreur est survenue. Réessayez.';

/**
 * Traduit une erreur de formulaire /auth (login/register) en message affichable —
 * factorisé ici (packages/ui) plutôt que dupliqué entre web-admin et web-parent,
 * chacun ayant son propre Login.tsx/Register.tsx mais la même logique de
 * classification d'erreur.
 *
 * Journalise TOUJOURS l'erreur réelle via `console.error`, AVANT toute
 * traduction — l'absence de ce log (bug réel constaté : le catch de Register.tsx
 * n'appelait jamais console.error) rendait un vrai échec CORS quasiment
 * impossible à diagnostiquer depuis l'extérieur, puisque `fetch()` ne renvoie
 * qu'un `TypeError: Failed to fetch` totalement opaque, IDENTIQUE qu'il s'agisse
 * d'une vraie coupure réseau ou d'un rejet CORS (limitation de la plateforme
 * fetch, pas de notre code — le navigateur ne communique jamais la raison exacte
 * d'un blocage CORS à JavaScript, par design de sécurité). Voir
 * docs/architecture.md, section "AUT-01", pour le détail complet du bug réel qui
 * a motivé cet ajout : CORS_ALLOWED_ORIGINS doit correspondre EXACTEMENT à
 * l'origine du navigateur (`http://127.0.0.1:8180` et `http://localhost:8180`
 * sont deux origines DIFFÉRENTES pour CORS, même si elles pointent vers le même
 * serveur) — `curl` ne détecte JAMAIS ce genre de désaccord, puisque `curl` ne
 * fait pas respecter CORS : seul un vrai navigateur (ou un `fetch()` exécuté
 * depuis un vrai contexte de page) le révèle.
 *
 * Distingue trois cas :
 * - `ApiError` avec un corps `{ message }` exploitable (le serveur A répondu) —
 *   le message backend est repris TEL QUEL, jamais reformulé (anti-énumération
 *   pour le 401 générique ; le 429 verrouillage est marqué `kind: 'locked'` pour
 *   un habillage visuel distinct, voir Login.tsx).
 * - `TypeError` (le serveur n'a JAMAIS été atteint — réseau coupé, ou requête
 *   bloquée par CORS avant même de partir) — message distinct qui oriente vers
 *   un problème de connectivité/configuration plutôt que vers une saisie
 *   utilisateur incorrecte.
 * - tout le reste — message générique de dernier recours.
 */
export function describeAuthFormError(err: unknown): AuthFormError {
  console.error('[auth] échec de la requête :', err);

  if (err instanceof ApiError && isAuthErrorBody(err.body)) {
    return { kind: err.status === 429 ? 'locked' : 'generic', message: err.body.message };
  }
  if (err instanceof TypeError) {
    return { kind: 'network', message: NETWORK_ERROR_MESSAGE };
  }
  return { kind: 'generic', message: UNKNOWN_ERROR_MESSAGE };
}
